import {debounce} from '../utils/debounce';

const selectors = {
  section: 'main .shopify-section',
  innerSection: '[data-section-type]',
  headerSection: '[data-section-type="header"]',
};

const classes = {
  sectionWhiteBg: 'shopify-section--white-bg',
  sentinel: 'section-sentinel',
};

export default function initSectionOverlayScrollEffect() {
  if (!window.theme.enableSectionOverlayScrolling || Shopify.designMode) return;

  const excludedPages = ['template-page-find-your-fit'];
  if (excludedPages.some((className) => document.body.classList.contains(className))) return;

  // Observe and update last section on the page
  const sectionsAll = Array.from(document.querySelectorAll(selectors.section));
  const sectionsToAnimate = [sectionsAll[sectionsAll.length - 1]];

  // Observe and update last two sections on the article page
  if (document.body.classList.contains('template-article')) {
    const sectionSecondToLast = sectionsAll[sectionsAll.length - 2];
    if (sectionSecondToLast.classList.contains('shop-the-story')) {
      sectionsToAnimate.unshift(sectionSecondToLast);
    }
  }

  const desktopQuery = window.matchMedia(`(min-width: ${window.theme.breakpoints.lg}px)`);
  const observers = [];

  const initObservers = () => {
    const isCollectionPage = document.body.classList.contains('template-collection');
    const isSearchPage = document.body.classList.contains('template-search');
    const paddingBottomDesktop = isCollectionPage || isSearchPage ? '390px' : '100px';
    const paddingBottomMobile = isCollectionPage || isSearchPage ? '72px' : '48px';

    sectionsToAnimate.forEach((section) => {
      const innerSection = section.querySelector(selectors.innerSection);
      if (!innerSection) return;

      innerSection.style.paddingBottom = desktopQuery.matches ? paddingBottomDesktop : paddingBottomMobile;
      section.style.height = `${innerSection.offsetHeight}px`;

      section.classList.add(classes.sectionWhiteBg);

      let rootMargin = '0px 0px -100%';
      let stickyClass = 'is-sticky-top';

      if (document.body.classList.contains('has-sticky-header')) {
        const headerSection = document.querySelector(selectors.headerSection);
        rootMargin = `0px 0px -${window.innerHeight - headerSection.offsetHeight}px`;
      }

      const isStickyBottom = innerSection.offsetHeight > window.innerHeight;

      if (isStickyBottom) {
        rootMargin = `0px 0px 0px 0px`;
        stickyClass = 'is-sticky-bottom';

        const sentinel = document.createElement('div');
        sentinel.classList.add(classes.sentinel);
        section.appendChild(sentinel);
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const isMobile = !window.matchMedia(`(min-width: ${window.theme.breakpoints.lg}px)`).matches;

            if (isMobile) {
              return;
            }

            innerSection.classList.toggle(stickyClass, entry.isIntersecting);
          });
        },
        {
          rootMargin,
        }
      );

      observers.push(observer);

      if (isStickyBottom) {
        const sentinel = section.querySelector(`.${classes.sentinel}`);
        if (!sentinel) return;
        observer.observe(sentinel);
        return;
      }

      observer.observe(section);
    });
  };

  const disconnectObservers = () => {
    observers?.forEach((observer) => observer.disconnect());
    sectionsToAnimate.forEach((section) => {
      const innerSection = section.querySelector(selectors.innerSection);
      innerSection.classList.remove('is-sticky-top', 'is-sticky-bottom');
    });
  };

  initObservers();

  // Disconnect and reinitialise observers when products on collection page are updated (e.g. after filtering)
  document.addEventListener('results-grid:loaded', () => {
    disconnectObservers();
    initObservers();
  });

  // Disconnect and reinitialise observers on window resize
  const debouncedWindowResize = debounce(() => {
    disconnectObservers();

    setTimeout(() => {
      initObservers();
    }, 75);
  }, 100);

  // Disconnect and reinitialise observers on tab change (as section height may change)
  sectionsToAnimate.forEach((section) => {
    section.querySelectorAll('.js-tablist__link')?.forEach((tabLink) => {
      tabLink.addEventListener('click', () => {
        disconnectObservers();
        initObservers();
      });
    });
  });

  window.addEventListener('resize', debouncedWindowResize);
}
